import React, { Component } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import numeral from 'numeral';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { max } from 'lodash';
import { Series, SeriesField } from './../../../core/types/Series';
import { ColorScale } from './../../../core/utils/Chart';

import APSeriesChartStyles from './styles/APSeriesChart.style';
import { TriggerGoogleAnalytics4Tag, TriggerGoogleAnalyticsTag } from '../../../core/utils/GoogleAnalyticTag';
import { GoogleAnalyticTags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalyticsTags';
import { GRAPH_DEFAULT_COLOR } from '../../../core/utils/Constants/Constants';
import { GoogleAnalytics4Tags } from '../../../core/utils/GoogleAnalyticTag/GoogleAnalytics4Tags';

interface APSeriesChartProps extends WithStyles<typeof APSeriesChartStyles> {
  height: number | string,
  legend: boolean,
  series: Array<Series> | null,
  width: number | string,
  labelHeading?: string,
  yPrefix?: string,
  xSuffixThisYear?: boolean,
}

type GenericObject = {
  [key: string]: boolean
};

type ColorObject = {
  [key: string]:string
}

const REFERRALS_RADIO_BUTTON_TAG_MAPPING = {
  'referrals': 'referrals',
  'registrations': 'registrations',
  'traded-accounts': 'tradedAccounts'
};

class APSeriesChart extends Component<APSeriesChartProps, {}> {
  public static defaultProps = {
    legend: true
  };

  state = {
    hiddenLineCharts: {},
    fieldColors: {}
  };

  constructor(props: any) {
    super(props);
    this.onClickLegend = this.onClickLegend.bind(this);
    this.setColor = this.setColor.bind(this);
  }

  onClickLegend(fieldName: string) {
    return (evt: any) => {
      const hiddenLineCharts: GenericObject = this.state.hiddenLineCharts;

      let hiddenValue: boolean = true;
      if (hiddenLineCharts[fieldName]) {
        hiddenValue = false;
      }
      const url = window.location.href;
      if (url.indexOf("/referrals") >= 0) {
        if(GoogleAnalyticTags.referrals.click.radioButton[REFERRALS_RADIO_BUTTON_TAG_MAPPING[fieldName]]) {
          TriggerGoogleAnalyticsTag(
              GoogleAnalyticTags.eventAction.click,
              GoogleAnalyticTags.referrals.click.radioButton[REFERRALS_RADIO_BUTTON_TAG_MAPPING[fieldName]]
          );
          TriggerGoogleAnalytics4Tag(
            GoogleAnalytics4Tags.referrals.name,
            GoogleAnalytics4Tags.eventAction.click,
            GoogleAnalytics4Tags.referrals.click.radioButton[REFERRALS_RADIO_BUTTON_TAG_MAPPING[fieldName]]
          );
        }
      }

      this.setState({
        hiddenLineCharts: {
          ...this.state.hiddenLineCharts,
          [fieldName]: hiddenValue
        }
      });
    }
  }

  getChartData() {

    const {
      series
    } = this.props;

    const chart_data: any = [];
    if (series !== null) {
      series.forEach(event => {
        const event_data: any = {
          name: event.event.eventLabel
        };

        event.fields.forEach((field: SeriesField) => {
          event_data[field.fieldName] = field.fieldValue;
        });

        chart_data.push(event_data);
      });
    }
    return chart_data;
  }

  getTicksData() {

    const {
      series
    } = this.props;

    const defaultTickValueArray = [0, 1, 2, 3, 4],
          ticksData: any = [];

    if (series !== null) {
      series.forEach(event => {
        event.fields.forEach((field: SeriesField) => {
          field.fieldValue && ticksData.push(field.fieldValue);
        });
      });
    }

    let maxValue:any = max(ticksData);

    if(!maxValue || maxValue === 0) {
      return [];
    }

    if(maxValue && maxValue >= 4) {
      let percentageAmount = Math.round((maxValue / 10 + maxValue)),
          multiplier = Math.pow(10, (percentageAmount.toString().length > 0 ?
                        (percentageAmount.toString().length -1) : 0)),
          ceilValue = Math.ceil(percentageAmount/multiplier)*multiplier,
          divider = 4;

      while(ceilValue % 4 !== 0 && ceilValue % 5 !== 0) {
        ceilValue += 1;
      }

      if(ceilValue % 4 === 0 && ceilValue % 5 === 0) {
        let tickValue = ceilValue / divider;
        return defaultTickValueArray.map((multiplierValue) => (tickValue * multiplierValue));
      }
    } else {
      return defaultTickValueArray;
    }
  }

  getFields() {
    const {
      series
    } = this.props;

    const order: any = [];
    if (series !== null) {
      series.forEach(event => {
        event.fields.forEach((field: SeriesField) => {
          let item = order.find((entry: any) => entry.fieldName === field.fieldName);
          if (!item) {
            item = {
              fieldLabel: field.fieldLabel,
              fieldName: field.fieldName,
              count: 0
            };

            order.push(item);
          }

          item.count += field.fieldValue;
        });
      })
    }

    return order;
  }

  setColor(fieldName: any) {
    const { fieldColors } = this.state;
    const color = Object.keys(fieldColors).length > 0 ?
      fieldColors[fieldName] : GRAPH_DEFAULT_COLOR;
    return color;
  }
  
  componentDidMount() {
    const {
      legend,
    } = this.props;

    const fields = this.getFields();
    const allFieldColor:ColorObject = {};
    const color = ColorScale();

    legend && fields.forEach((field: any) => {
      allFieldColor[field.fieldName] = color(field.fieldName);
    });

    this.setState({ fieldColors: allFieldColor });
  }
   
  render() {
    const {
      classes,
      height,
      legend,
      series,
      width,
      labelHeading,
      yPrefix,
      xSuffixThisYear
    } = this.props;

    if (series === null) {
      return null;
    }

    const fields = this.getFields();

    const chartMargin = {
      top: 10,
      right: 40,
      bottom: 0,
      left: 0
    };

    const formatter = (value) => `${(yPrefix) ? yPrefix : ''}${numeral(value).format('0,000')}`;
    const xFormatter = (value) => `${value}${(xSuffixThisYear) ? " '" + new Date().getFullYear().toString().substring(2) : ''}`;

    return (
      <div className={classes.chartContainer}>
        {legend && fields.length > 0 &&
          <div key={'series-legend'} className={classes.chartLegend}>
            {labelHeading && <h5>{labelHeading}</h5>}
            {legend && fields.map((field: any) => {
              const fieldColor = this.setColor(field.fieldName);
              const hiddenLineCharts: GenericObject = this.state.hiddenLineCharts;

              const styles = {
                'backgroundColor': hiddenLineCharts[field.fieldName] ? 'transparent' : fieldColor,
                'borderColor': fieldColor
              };

              return (
                <div key={`legend-${field.fieldName}`} className={classes.chartLegendItem} onClick={this.onClickLegend(field.fieldName)}>
                  <span className={classes.chartLegendItemKey} style={styles} data-testid='legend-color'></span>
                  <span className={classes.chartLegendItemName}>
                    {field.fieldLabel}
                  </span>
                </div>
              );
            })}
          </div>
        }
        <ResponsiveContainer key={'series-chart'} height={height} width={width} className={classes.chartArea}>
          <AreaChart data={this.getChartData()} margin={chartMargin}>
            <defs>
              {fields.map((field: any) => (
                <linearGradient id={`color-${field.fieldName}`} x1="0" y1="0" x2="0" y2="1" key={`color-${field.fieldName}`}>
                  <stop offset="5%" stopColor={this.setColor(field.fieldName)} stopOpacity={0.5} />
                  <stop offset="95%" stopColor={this.setColor(field.fieldName)} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <XAxis tickFormatter={xFormatter} dataKey="name" stroke="#A8A8A8" dy={10} />
            <YAxis tickFormatter={formatter} stroke="#A8A8A8" dx={0} ticks={this.getTicksData()}/>
            <CartesianGrid stroke="#A8A8A8" strokeWidth={0.5} />
            <Tooltip />
            {fields.map((field: any) => {
              const hiddenLineCharts: GenericObject = this.state.hiddenLineCharts;
              if (hiddenLineCharts[field.fieldName]) {
                return null;
              }

              const dotBase = {
                stroke: this.setColor(field.fieldName),
                strokeWidth: 1.8,
                fill: '#FFF',
                fillOpacity: 1,
                r: 3.5
              };

              return (
                <Area
                  key={`area-${field.fieldName}`}
                  type="monotone"
                  dataKey={field.fieldName}
                  activeDot={{
                    ...dotBase,
                    fill: this.setColor(field.fieldName)
                  }}
                  dot={dotBase}
                  fill={`url(#color-${field.fieldName})`}
                  stroke={this.setColor(field.fieldName)}
                  strokeWidth={1.8}
                  /* stackId={field.fieldName} */
                  isAnimationActive={false}
                />
              );
            })}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }
}

export default withStyles(APSeriesChartStyles)(APSeriesChart);
